import { fetchCallCenterDevices, filterArrayDeviceIssue } from '@/entities/device';
import { getModelReasonOptions, toOptionEntry, useRmaStore } from '@/entities/rma';
import { useMergeState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { getOptionsMissingDescribe } from '../../lib/addRmaDeviceModal';
import type { DeviceInformationProps } from './index.types';

const noReplaceOption = { label: 'No replacement', value: 'No replacement' };

/**
 * Manages the device issue list of the new RMA form and its add or edit device modal.
 * Loads the facility's active renewal devices as replacements, seeds the list from
 * initialRmaDevices, marks each issue complete or not, and reports the list through
 * onChangeDataDeviceRMA whenever it changes.
 *
 * @param props - Device information props: selectedFacility, onChangeDataDeviceRMA and
 *   initialRmaDevices.
 * @returns An object carrying the device issue state, the chosen device and replacement ids, and
 *   the remove, update, drawer, modal, replacement-option and edit handlers.
 */
const useDeviceInformation = ({
  selectedFacility,
  onChangeDataDeviceRMA,
  initialRmaDevices,
}: Pick<
  DeviceInformationProps,
  'selectedFacility' | 'onChangeDataDeviceRMA' | 'initialRmaDevices'
>) => {
  const rmaOptionLists = useRmaStore((s) => s.rmaOptionLists);
  const allReasonOptions = _.flatMap(rmaOptionLists.reasons);

  const [state, setState] = useMergeState<any>({
    deviceIssueArray: [],
    selectedDeviceIssuesArr: [],
    errorObject: {},
    isVisiblePatientImpactDrawer: false,
    replacedDevices: [noReplaceOption],
    isOpenAddRMADeviceModal: false,
    deviceIssue: {},
    initialDeviceIssue: {},
    deviceIssueIndex: null,
    isLoading: false,
  });

  /**
   * The device ids already chosen on the RMA form. Memoized from state.deviceIssueArray by reading each entry's deviceId value, so the device search can exclude them.
   *
   * @returns The selected device ids.
   */
  const listCurrentDeviceIds = useMemo(() => {
    /**
     * The device id values pulled out of the current device issues. Derived by mapping each entry to its deviceId value.
     *
     * @returns The selected device ids.
     */
    const result = _.map(state.deviceIssueArray, (item) => item?.deviceId?.value);
    return result;
  }, [state.deviceIssueArray]);
  /**
   * The replacement device ids already chosen on the RMA form. Memoized from state.deviceIssueArray by reading each entry's replacementDeviceId value, so replacement options can exclude them.
   *
   * @returns The selected replacement device ids.
   */
  const listCurrentReplacementDeviceIds = useMemo(() => {
    /**
     * The replacement device id values pulled out of the current device issues. Derived by mapping each entry to its replacementDeviceId value.
     *
     * @returns The selected replacement device ids.
     */
    const result = _.map(state.deviceIssueArray, (item) => item?.replacementDeviceId?.value);
    return result;
  }, [state.deviceIssueArray]);

  /**
   * Drops one device issue row from the RMA device information list. Clones the issue array, splices out the given index and stores the shortened array.
   *
   * @param index - Row position of the device issue to remove.
   * @returns Nothing.
   */
  const handleRemoveDeviceIssue = (index: any) => {
    const { deviceIssueArray } = state;
    const tempArr = _.cloneDeep(deviceIssueArray);
    tempArr.splice(index, 1);
    setState({ deviceIssueArray: tempArr });
  };

  /**
   * Replaces the RMA device issue list after the add or edit modal saved. Stores the supplied array as deviceIssueArray.
   *
   * @param newDeviceIssueArray - Complete replacement list of device issues.
   * @returns Nothing.
   */
  const handleUpdateDeviceIssue = (newDeviceIssueArray: any) => {
    setState({ deviceIssueArray: newDeviceIssueArray });
  };

  /**
   * Opens or closes the patient impact drawer of the RMA device information step. Inverts the isVisiblePatientImpactDrawer flag.
   *
   * @returns Nothing.
   */
  const toggleDisplayDrawer = () => {
    setState({ isVisiblePatientImpactDrawer: !state.isVisiblePatientImpactDrawer });
  };

  /**
   * Closes the add RMA device modal. Sets isOpenAddRMADeviceModal to false.
   *
   * @returns Nothing.
   */
  const closeAddRMADeviceModal = () => {
    setState({ isOpenAddRMADeviceModal: false });
  };

  /**
   * Opens the add RMA device modal for a brand-new device issue. Shows the modal with an empty initial issue and no edit index.
   *
   * @returns Nothing.
   */
  const onClickAddMoreButton = () => {
    setState({
      isOpenAddRMADeviceModal: true,
      initialDeviceIssue: {},
      deviceIssueIndex: null,
    });
  };

  useEffect(() => {
    /**
     * Loads the active renewal devices offered as replacements on an RMA. Fetches call-center devices for the selected facility filtered to renewal and Active status and sorted by deviceId, stores them as replacedDevices, and clears loading and logs the failure on error.
     *
     * @returns Resolves once the replacement devices or the failure state are stored.
     */
    const fetchReplacedDevice = async () => {
      try {
        setState({ isLoading: true });
        const queryFilter = {
          sortBy: 'deviceId',
          sortOrder: 'desc',
          facilityId: selectedFacility.value,
          isRenewal: true,
          additionalStatus: 'Active',
        };
        const deviceInfo = await fetchCallCenterDevices(queryFilter, 0);
        setState({
          replacedDevices: deviceInfo,
          isLoading: false,
        });
      } catch (error) {
        setState({ isLoading: false });
        console.error('Failed to fetch replaced device: ', error);
      }
    };
    if (selectedFacility) {
      fetchReplacedDevice();
    }
  }, [selectedFacility]);

  useEffect((): any => {
    const tempArr: any = [];
    _.forEach(state.deviceIssueArray, (item) => {
      if (item.deviceId) {
        tempArr.push(item.deviceId);
      }
      if (item.replacementDeviceId) {
        tempArr.push(item.replacementDeviceId);
      }
      if (
        !item?.deviceId ||
        !item?.reasons?.length ||
        !_.isEmpty(
          getOptionsMissingDescribe(
            item?.reasons,
            getModelReasonOptions(rmaOptionLists.reasons, item?.deviceModel),
          ),
        ) ||
        !item.dateOfEvent ||
        !item.patientImpact
      ) {
        item.isComplete = false;
      } else {
        item.isComplete = true;
      }
    });

    setState({ selectedDeviceIssuesArr: tempArr });
    onChangeDataDeviceRMA(state.deviceIssueArray);
  }, [state.deviceIssueArray]);

  useEffect(() => {
    if (_.isEmpty(initialRmaDevices)) {
      setState({
        deviceIssueArray: [],
        selectedDeviceIssuesArr: [],
      });
    }
  }, [selectedFacility, initialRmaDevices]);

  useEffect((): any => {
    if (!_.isEmpty(initialRmaDevices)) {
      const formattedRmaDevices: any = [];
      _.forEach(initialRmaDevices, (item) => {
        formattedRmaDevices.push({
          deviceId: { label: item.deviceId, value: item.deviceId },
          replacementDeviceId: item.replacedDevice
            ? {
                slabel: item.replacedDevice.deviceId,
                svalue: item.replacedDevice.deviceId,
              }
            : null,
          reasons: _.map(item.reasons, (x) => toOptionEntry(allReasonOptions, x)),
          patientImpact: item.patientImpact
            ? toOptionEntry(rmaOptionLists.patientImpacts, item.patientImpact)
            : null,
          inputValue: '',
          dateOfEvent: item.dateOfEvent ? dayjs(item.dateOfEvent) : null,
          isComplete: true,
          isInitial: true,
          deviceModel: item.deviceModel,
          lostDeviceNeedReplacement: item.lostDeviceNeedReplacement,
        });
      });
      setState({
        deviceIssueArray: _.isEmpty(formattedRmaDevices) ? [] : formattedRmaDevices,
      });
    }
  }, [initialRmaDevices]);

  /**
   * Builds the replacement device dropdown options for one RMA device issue. Starts from the no-replace option and adds the loaded replacement devices, restricted to the same device model when the issue names one, then removes the devices already picked by other issues.
   *
   * @param item - Device issue the options are built for.
   * @returns Dropdown options for the replacement device, excluding already chosen devices.
   */
  const getReplaceDeviceOptions = (item: any) => {
    const options = [noReplaceOption];
    if (item?.deviceModel) {
      _.forEach(state.replacedDevices, (replaceDevice) => {
        if (replaceDevice.model === item.deviceModel) {
          options.push({
            label: replaceDevice.deviceId,
            value: replaceDevice.deviceId,
          });
        }
      });
      return filterArrayDeviceIssue(options, state.selectedDeviceIssuesArr);
    }
    _.forEach(state.replacedDevices, (replaceDevice) => {
      options.push({
        label: replaceDevice.deviceId,
        value: replaceDevice.deviceId,
      });
    });
    return filterArrayDeviceIssue(options, state.selectedDeviceIssuesArr);
  };

  /**
   * Opens the add RMA device modal to edit an existing device issue. Shows the modal seeded with the chosen issue and remembers its row index so the save replaces that row.
   *
   * @param item - Device issue to edit.
   * @param index - Row position of that issue.
   * @returns Nothing.
   */
  const onClickEditButton = (item: any, index: any) => {
    setState({
      isOpenAddRMADeviceModal: true,
      initialDeviceIssue: item,
      deviceIssueIndex: index,
    });
  };

  return {
    state,
    listCurrentDeviceIds,
    listCurrentReplacementDeviceIds,
    handleRemoveDeviceIssue,
    handleUpdateDeviceIssue,
    toggleDisplayDrawer,
    closeAddRMADeviceModal,
    onClickAddMoreButton,
    getReplaceDeviceOptions,
    onClickEditButton,
  };
};

export { useDeviceInformation };
